// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Region } from './types';
import { num, delta, pct, people, REMOTENESS_SHORT } from './format';

export interface Insight {
  severity: 'alert' | 'warn' | 'good' | 'info';
  title: string;
  body: string;
  link?: { view: string; region?: string; bank?: string; filter?: string; label: string };
}

export function buildInsights(data: Dataset): Insight[] {
  const { national, regions, institutions, meta } = data;
  const c = meta.counts;
  const N = national.years.length;
  const out: Insight[] = [];

  out.push({
    severity: 'alert',
    title: `Nearly half of Australia's branches are gone`,
    body: `Branches fell from ${num(c.branches2017)} in ${meta.firstYear} to ${num(c.branchesNow)} in ${meta.latestYear} — ${delta(c.branchChangePct)}. That is roughly ${num(Math.round((c.branches2017 - c.branchesNow) / (N - 1)))} branches shut every year for ${N - 1} years.`,
    link: { view: 'timeline', label: 'See the timeline' },
  });

  out.push({
    severity: 'warn',
    title: `${num(c.sa2LostLastCount)} towns have lost their last branch`,
    body: `About ${people(c.popAffected)} people live in a town that had a bank branch in ${meta.firstYear} and has none today. ${num(c.sa2LostLastNothing)} of those towns have no Bank@Post either — no staffed banking of any kind.`,
    link: { view: 'deserts', label: 'Open Banking deserts' },
  });

  out.push({
    severity: 'good',
    title: `Bank@Post is holding the line`,
    body: `While branches fell ${delta(c.branchChangePct)} and bank ATMs ${delta(c.atmChangePct)}, Bank@Post barely moved (${delta(c.bankpostChangePct)}). In ${num(c.sa2LostLastWithBankpost)} branchless towns the local post office is now the only counter left.`,
    link: { view: 'remoteness', label: 'See who relies on it' },
  });

  // fastest-retreating remoteness class (by branch % change)
  const remChange = Object.entries(national.byRemoteness)
    .map(([k, s]) => ({ k, first: s.branch[0], last: s.branch[N - 1] }))
    .filter((r) => r.first > 0)
    .map((r) => ({ ...r, ch: (r.last - r.first) / r.first }))
    .sort((a, b) => a.ch - b.ch);
  if (remChange.length) {
    const w = remChange[0];
    out.push({
      severity: 'warn',
      title: `${REMOTENESS_SHORT[w.k] ?? w.k} has been hit hardest`,
      body: `Branches in ${REMOTENESS_SHORT[w.k] ?? w.k} Australia fell ${delta(w.ch)} since ${meta.firstYear} (${num(w.first)} → ${num(w.last)}) — the steepest of the five remoteness bands, where alternatives are already scarcest.`,
      link: { view: 'remoteness', label: 'Compare remoteness bands' },
    });
  }

  // fastest-retreating major bank (by absolute branches lost)
  const bankRetreat = institutions
    .filter((i) => i.branch2017 >= 50)
    .map((i) => ({ i, lost: i.branch2017 - i.branchNow }))
    .sort((a, b) => b.lost - a.lost);
  if (bankRetreat.length) {
    const b = bankRetreat[0];
    out.push({
      severity: 'info',
      title: `${b.i.name.replace(/\s+(Limited|Ltd)\.?$/i, '')} closed the most branches`,
      body: `It went from ${num(b.i.branch2017)} branches in ${meta.firstYear} to ${num(b.i.branchNow)} today — ${num(b.lost)} gone (${delta(b.i.branchChange)}).`,
      link: { view: 'banks', bank: b.i.abn, label: 'Open this bank' },
    });
  }

  // worst-served populated region by per-capita access
  const served = regions.filter((r) => r.pop && r.pop > 20000 && r.perCapita != null);
  const lowest = [...served].sort((a, b) => (a.perCapita! - b.perCapita!))[0];
  if (lowest) {
    out.push({
      severity: 'info',
      title: `Thinnest branch coverage: ${lowest.name}`,
      body: `${lowest.name} (${lowest.state}) has just ${num(lowest.branchNow)} branches for ${num(lowest.pop)} residents — ${lowest.perCapita!.toFixed(2)} per 10,000, against a national picture of one branch per ${num(Math.round((regions.reduce((a, r) => a + (r.pop || 0), 0)) / Math.max(1, c.branchesNow)))} people.`,
      link: { view: 'map', region: lowest.code, label: 'Show on the map' },
    });
  }

  // region that lost the most branches in absolute terms
  const biggestLoss = [...regions].sort((a, b) => a.branchChangeAbs - b.branchChangeAbs)[0];
  if (biggestLoss && biggestLoss.branchChangeAbs < 0) {
    out.push({
      severity: 'warn',
      title: `${biggestLoss.name} lost the most branches`,
      body: `${biggestLoss.name} (${biggestLoss.state}) is down ${num(-biggestLoss.branchChangeAbs)} branches since ${meta.firstYear}, from ${num(biggestLoss.branch2017)} to ${num(biggestLoss.branchNow)}.`,
      link: { view: 'rankings', region: biggestLoss.code, label: 'Open this region' },
    });
  }

  // very remote reliance on Bank@Post
  const vr = national.byRemoteness['Very Remote Australia'];
  if (vr) {
    const bp = vr.bankpost[N - 1];
    const br = vr.branch[N - 1];
    const total = bp + br + vr.other[N - 1];
    if (total > 0) {
      out.push({
        severity: 'info',
        title: `In Very Remote Australia, most counters are a post office`,
        body: `Of the ${num(total)} staffed banking points in Very Remote Australia, ${num(bp)} (${pct(bp / total, 0)}) are Bank@Post and only ${num(br)} are full bank branches.`,
        link: { view: 'remoteness', label: 'See the remoteness mix' },
      });
    }
  }

  return out;
}

/** Regions sorted for the "biggest losers" rail — used by deserts + rankings. */
export function biggestBranchLosers(regions: Region[], n = 12): Region[] {
  return [...regions].filter((r) => r.branchChangeAbs < 0).sort((a, b) => a.branchChangeAbs - b.branchChangeAbs).slice(0, n);
}
